import React, { useState, useEffect, useCallback } from 'react';
import { getAnalytics } from '../../services/adminService';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import '../../styles/Analytics.css';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function Analytics() {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnalytics(range);
      if (res.success) setData(res.data);
    } catch { setError('Erreur lors du chargement des analytics'); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>Tableau de bord analytique</h1>
        <div className="range-selector">
          {['7d', '30d', '90d'].map(r => (
            <button
              key={r}
              className={`btn btn-sm ${range === r ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setRange(r)}
            >
              {r === '7d' ? '7 jours' : r === '30d' ? '30 jours' : '90 jours'}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="analytics-grid">
          <LoadingSkeleton lines={6} />
          <LoadingSkeleton lines={6} />
          <LoadingSkeleton lines={6} />
          <LoadingSkeleton lines={6} />
        </div>
      ) : data ? (
        <>
          {/* KPI cards */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-value">{data.totalVisits ?? 0}</div>
              <div className="kpi-label">Total visites</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value">{data.uniqueVisitors ?? 0}</div>
              <div className="kpi-label">Visiteurs uniques</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value">{data.currentOccupancy ?? 0}</div>
              <div className="kpi-label">Occupants actuels</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value">{data.avgDaily ?? 0}</div>
              <div className="kpi-label">Moy. quotidienne</div>
            </div>
          </div>

          <div className="analytics-grid">
            {/* Daily registrations */}
            {data.dailyRegistrations && (
              <div className="chart-card">
                <h3>Inscriptions quotidiennes</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.dailyRegistrations}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Visit type breakdown */}
            {data.visitTypeBreakdown && (
              <div className="chart-card">
                <h3>Répartition par type</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={data.visitTypeBreakdown} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={100} label>
                      {data.visitTypeBreakdown.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Peak hours */}
            {data.peakHours && (
              <div className="chart-card">
                <h3>Heures de pointe</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Event fill rates */}
            {data.eventFillRates && (
              <div className="chart-card">
                <h3>Taux de remplissage des événements</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.eventFillRates} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="title" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="fillRate" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>

  );
}
